import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import { trackDownload } from '../services/api';

// Configurer le worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString();

export default function SimplePDFViewer({ pdfConfig }) {
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Charger les paramètres depuis localStorage
  const loadSettings = () => {
    const saved = localStorage.getItem('pdfViewerSettings');
    return saved ? JSON.parse(saved) : { defaultZoom: 180, allowDownload: true };
  };
  
  const settings = loadSettings();
  
  const [pdf, setPdf] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(settings.defaultZoom / 100); // Utiliser le zoom des paramètres
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    console.log('📄 SimplePDFViewer: Chargement du PDF...', pdfConfig);
    
    // Charger le PDF
    const loadingTask = pdfjsLib.getDocument({
      url: pdfConfig.url,
      httpHeaders: pdfConfig.httpHeaders,
    });

    loadingTask.promise
      .then((pdfDoc) => {
        console.log('✅ PDF chargé, pages:', pdfDoc.numPages);
        setPdf(pdfDoc);
        setNumPages(pdfDoc.numPages);
        setLoading(false);
      })
      .catch((err) => {
        console.error('❌ Erreur chargement PDF:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [pdfConfig]);

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;

    console.log('🎨 Rendu de la page', pageNum);

    let renderTask = null;

    pdf.getPage(pageNum).then((page) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const context = canvas.getContext('2d');
      
      // Utiliser devicePixelRatio pour une meilleure résolution
      const devicePixelRatio = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: scale * devicePixelRatio });
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.style.width = `${viewport.width / devicePixelRatio}px`;
      canvas.style.height = `${viewport.height / devicePixelRatio}px`;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      // Annuler le rendu précédent s'il existe
      if (renderTask) {
        renderTask.cancel();
      }

      renderTask = page.render(renderContext);
      
      renderTask.promise
        .then(() => {
          console.log('✅ Page', pageNum, 'rendue');
        })
        .catch((err) => {
          if (err.name === 'RenderingCancelledException') {
            console.log('⚠️ Rendu annulé (normal lors du changement de page)');
          } else {
            console.error('❌ Erreur rendu:', err);
          }
        });
    });

    // Cleanup : annuler le rendu si le composant change
    return () => {
      if (renderTask) {
        renderTask.cancel();
      }
    };
  }, [pdf, pageNum, scale]);

  // Fonction de téléchargement
  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Tracker le téléchargement
      const token = searchParams.get('token');
      if (token) {
        await trackDownload(token);
        console.log('📊 Téléchargement tracké');
      }
      
      const response = await fetch(pdfConfig.url, {
        headers: pdfConfig.httpHeaders,
      });
      
      // Extraire le nom de fichier du header Content-Disposition
      let downloadFilename = 'CV.pdf'; // Valeur par défaut
      const contentDisposition = response.headers.get('Content-Disposition');
      console.log('🔍 Content-Disposition header:', contentDisposition);
      
      if (contentDisposition) {
        // Essayer d'abord le format UTF-8 (filename*=UTF-8''...)
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        console.log('🔍 UTF-8 match:', filenameStarMatch);
        
        if (filenameStarMatch) {
          downloadFilename = decodeURIComponent(filenameStarMatch[1]);
          console.log('✅ Nom extrait (UTF-8):', downloadFilename);
        } else {
          // Sinon, essayer le format classique (filename="...")
          const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/i);
          console.log('🔍 Classic match:', filenameMatch);
          
          if (filenameMatch) {
            downloadFilename = filenameMatch[1];
            console.log('✅ Nom extrait (classic):', downloadFilename);
          }
        }
        console.log('📝 Nom de fichier final:', downloadFilename);
      } else {
        console.log('⚠️ Aucun header Content-Disposition trouvé');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      console.log('✅ PDF téléchargé:', downloadFilename);
    } catch (err) {
      console.error('❌ Erreur téléchargement:', err);
      alert('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Chargement du PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center text-red-600">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xl font-semibold">Erreur: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Barre de contrôles */}
      <div className="bg-white shadow-md p-4 flex items-center justify-between flex-wrap gap-4">
        {/* Bouton Fermer */}
        <button
          onClick={() => navigate('/thank-you')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center space-x-2"
          title="Quitter la session"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span>Fermer</span>
        </button>

        <div className="flex items-center space-x-4">
          <button
            onClick={() => setPageNum((p) => Math.max(p - 1, 1))}
            disabled={pageNum <= 1}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            ← Précédent
          </button>
          <span className="text-gray-700 font-medium">
            Page {pageNum} / {numPages}
          </span>
          <button
            onClick={() => setPageNum((p) => Math.min(p + 1, numPages))}
            disabled={pageNum >= numPages}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Suivant →
          </button>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={() => setScale((s) => Math.max(s - 0.2, 0.5))}
            disabled={scale <= 0.5}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            🔍−
          </button>
          <span className="text-gray-700 font-medium min-w-[60px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => setScale((s) => Math.min(s + 0.2, 2.0))}
            disabled={scale >= 2.0}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            🔍+
          </button>
          
          {/* Bouton télécharger (conditionnel) */}
          {settings.allowDownload && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
            >
            {downloading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Téléchargement...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Télécharger</span>
              </>
            )}
          </button>
          )}
        </div>
      </div>

      {/* Canvas pour le PDF */}
      <div className="flex-1 overflow-auto flex justify-center p-4 bg-gray-100">
        <canvas ref={canvasRef} className="shadow-lg bg-white" />
      </div>
    </div>
  );
}
